import FuseUtils from "@fuse/utils/FuseUtils";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
	IApiResponse,
	IForgotPasswordResponse,
} from "@fuse/models/user.models";
import { IPetModel } from "@fuse/models";

export interface PetAutoRenewalStatus {
	planName: string | null;
	annualPrice: number | null;
	currency: string;
	isLifetime: boolean;
	hasPlan: boolean;
	canManage: boolean;
	autoRenewalEnabled: boolean;
	protectedUntilUtc: string | null;
}

export interface PetRemovalDetails {
    petId: string;
    petName: string;
    isRemoved: boolean;
    auditId?: string;
    reason?: string;
}

class PetService {
    getOwnedRemovalDetails = async (petId: string): Promise<PetRemovalDetails> => {
        const { data } = await axios.get<IApiResponse>(`${process.env.VITE_APP_API_BASE_URL}Pet/${encodeURIComponent(petId)}/removal-details`);
        if (!data.succeeded) throw new Error(data.errors?.[0] || "Unable to verify this pet for removal.");
        const details = data.result as PetRemovalDetails;
        if (!details || details.petId !== petId || typeof details.isRemoved !== "boolean" || (details.isRemoved && !details.auditId))
            throw new Error("Unable to verify this pet for removal.");
        return details;
    };
    removeOwnedPet = async (petId: string, reason: string) => {
        const { data } = await axios.post<IApiResponse>(`${process.env.VITE_APP_API_BASE_URL}Pet/${petId}/remove`, { reason });
        if (!data.succeeded) throw new Error(data.errors?.[0] || "Unable to remove this pet.");
        return data.result;
    };
	getAutoRenewal = async (petId: string): Promise<PetAutoRenewalStatus> => {
		const { data } = await axios.get<IApiResponse>(
			`${process.env.VITE_APP_API_BASE_URL}Pet/${petId}/auto-renewal`
		);
		if (!data.succeeded) throw new Error(data.errors?.[0] || "Unable to load renewal details.");
		return data.result as PetAutoRenewalStatus;
	};

	setAutoRenewal = async (petId: string, enabled: boolean): Promise<PetAutoRenewalStatus> => {
		const { data } = await axios.put<IApiResponse>(
			`${process.env.VITE_APP_API_BASE_URL}Pet/${petId}/auto-renewal`,
			{ enabled }
		);
		if (!data.succeeded) throw new Error(data.errors?.[0] || "Unable to change renewal.");
		return data.result as PetAutoRenewalStatus;
	};
	/**
	 * Signs in with the provided email and password.
	 */
	getUserPets = () =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}pet/user-pets`)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data) {
						resolve(response.data.result);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	getUserPetList = () =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}pet/user-pet-list`)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data) {
						resolve(response.data);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	getPetById = (id: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}Pet/${id}`)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data) {
						resolve(response.data);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	getPetByIdAsGuest = (id: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}Pet/Guest/${id}`)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data) {
						resolve(response.data);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	getRecoverDetails = (id: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}Pet/Guest/${id}/recover`)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data?.succeeded) {
						resolve(response.data.result);
					} else if (response.data?.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	createPet = (data: any) =>
		new Promise((resolve, reject) => {
			axios
				.post(`${process.env.VITE_APP_API_BASE_URL}Pet/create-pet`, data)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data.succeeded) {
						resolve(response.data.result);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	updatePet = (id: string, data: any) =>
		new Promise((resolve, reject) => {
			axios
				.put(`${process.env.VITE_APP_API_BASE_URL}Pet/${id}`, data)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data.succeeded) {
						resolve(response.data.result);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	updatePetPlan = (id: string, data: any) =>
		new Promise((resolve, reject) => {
			axios
				.put(`${process.env.VITE_APP_API_BASE_URL}Pet/update-plan`, data)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data.succeeded) {
						resolve(response.data.result);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	updatePetProfile = (data: any) =>
		new Promise((resolve, reject) => {
			axios
				.put(`${process.env.VITE_APP_API_BASE_URL}Pet/pet-profile`, data)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data.succeeded) {
						resolve(response.data.result);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	getPetPlans = (promo?: string, addPet?: boolean) =>
		new Promise((resolve, reject) => {
			const params = new URLSearchParams();
			if (promo) params.append("promo", promo);
			if (addPet) params.append("addPet", "true");
			const query = params.toString();
			const url = `${process.env.VITE_APP_API_BASE_URL}pet/plans${query ? `?${query}` : ""}`;
			axios
				.get(url)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data) {
						resolve(response.data);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	cleanUserPetData = () =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}pet/remove-user-pets`)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	markAsRead = (petId: string) =>
		new Promise((resolve, reject) => {
			axios
				.put(
					`${process.env.VITE_APP_API_BASE_URL}pet/mark-as-found/${petId}`,
					{}
				)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	sendMailFromPetAlert = (obj: any) =>
		new Promise((resolve, reject) => {
			axios
				.post(`${process.env.VITE_APP_API_BASE_URL}checkout/checkout-lpa`, obj)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	alertByPet = (petId: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(
					`${process.env.VITE_APP_API_BASE_URL}petalert/alerts/bypet/${petId}`
				)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	getEmailStats = (lpaId: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}email/${lpaId}/getEmailStats`)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	getPetSubscriptionUrl = () =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}pet/microchip-subscriptions`)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data.result);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	upgradePetPlan = (petId: string) =>
		new Promise((resolve, reject) => {
			axios
				.post(
					`${process.env.VITE_APP_API_BASE_URL}checkout/upgrade-subscription/${petId}`,
					{}
				)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data.result);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	getPetProfilesData = (petId: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}pet/pet-profiles/${petId}`)
				.then((response: AxiosResponse<any>) => {
					resolve(response.data.result);
				})
				.catch((error) => {
					resolve(error);
				});
		});

	getPetByShortUrl = (url: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}pet/short-url-details/${url}`)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data) {
						resolve(response.data);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});

	getCollarPrice = () =>
		new Promise((resolve, reject) => {
			axios
				.get(`${process.env.VITE_APP_API_BASE_URL}pet/collar-tag-plan`)
				.then((response: AxiosResponse<IApiResponse>) => {
					if (response.data.succeeded) {
						resolve(response.data.result);
					} else if (response.data.errors) {
						reject(response.data.errors);
					} else {
						reject(response.data);
					}
				});
		});
}

const instance = new PetService();

export default instance;
